import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { PreventiveMaintenance } from '../../../../models/PreventiveMaintenance';
import { PreventiveMaintenanceService } from '../../../../services/preventive-maintenance.service';
import { TableAction } from '../../../../shared/components/table/table.component';

@Component({
  selector: 'app-preventive-maintenance-list',
  templateUrl: './preventive-maintenance-list.component.html',
  styleUrls: ['./preventive-maintenance-list.component.css']
})
export class PreventiveMaintenanceListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // ===== DADOS =====
  preventiveMaintenances: PreventiveMaintenance[] = [];
  filteredPreventiveMaintenances: PreventiveMaintenance[] = [];
  isLoading = false;
  error: string | null = null;

  // ===== FILTROS =====
  filters = {
    vehicleType: '',
    isActive: ''
  };

  vehicleTypes = [
    'Carro',
    'Moto',
    'Caminhão',
    'Van',
    'Ônibus',
    'Outros'
  ];

  // ===== CONFIGURAÇÃO DA TABELA =====
  tableTitle = 'Preventivas';
  showSearch = true;
  searchPlaceholder = 'Buscar por nome, tipo de veículo, marca, modelo...';
  clickable = true;

  columns = [
    { titulo: 'Nome', label: 'maintenanceName' },
    { titulo: 'Tipo Veículo', label: 'vehicleType' },
    { titulo: 'Descrição', label: 'description' },
    { titulo: 'Status', label: 'isActive', formatter: (value: any) => value ? 'Ativa' : 'Inativa' }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Ativar/Desativar',
      icon: 'fas fa-toggle-on',
      action: 'toggle',
      class: 'btn-toggle'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  constructor(
    private preventiveMaintenanceService: PreventiveMaintenanceService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadPreventiveMaintenances();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== CARREGAMENTO DE DADOS =====

  /**
   * Carrega todas as preventivas
   */
  loadPreventiveMaintenances(): void {
    this.isLoading = true;
    this.error = null;

    this.preventiveMaintenanceService.getAll(1, 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Mapear campos PascalCase da API para camelCase do modelo
          this.preventiveMaintenances = (response.data || []).map((item: any) => ({
            id: item.Id,
            maintenanceName: item.MaintenanceName,
            description: item.Description || undefined,
            instructions: item.Instructions || undefined,
            vehicleType: item.VehicleType || undefined,
            vehicleBrand: item.VehicleBrand || undefined,
            vehicleModel: item.VehicleModel || undefined,
            targetMileage: item.IntervalMileage || undefined,
            estimatedCost: item.EstimatedCost || undefined,
            estimatedDurationHours: item.EstimatedMinutes ? item.EstimatedMinutes / 60 : undefined,
            isActive: item.IsActive,
            isRequired: item.IsRequired || false,
            requiresSignature: item.RequiresSignature || false,
            orderIndex: item.Priority || undefined,
            createdAt: item.CreatedAt || undefined,
            updatedAt: item.UpdatedAt || undefined,
            createdBy: item.CreatedBy || undefined,
            updatedBy: item.UpdatedBy || undefined,
            items: item.Items || []
          }));
          this.applyFilters();
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Erro ao carregar preventivas:', err);
          this.error = 'Erro ao carregar preventivas. Tente novamente.';
          this.isLoading = false;
        }
      });
  }

  /**
   * Retry após erro
   */
  retryLoad(): void {
    this.loadPreventiveMaintenances();
  }

  // ===== FILTROS =====

  /**
   * Aplica os filtros selecionados
   */
  applyFilters(): void {
    let filtered = [...this.preventiveMaintenances];

    // Filtro por tipo de veículo
    if (this.filters.vehicleType) {
      filtered = filtered.filter(
        pm => pm.vehicleType === this.filters.vehicleType
      );
    }

    // Filtro por status
    if (this.filters.isActive !== '') {
      const isActive = this.filters.isActive === 'true';
      filtered = filtered.filter(pm => pm.isActive === isActive);
    }

    this.filteredPreventiveMaintenances = filtered;
  }

  /**
   * Limpa todos os filtros
   */
  clearFilters(): void {
    this.filters = {
      vehicleType: '',
      isActive: ''
    };
    this.applyFilters();
  }

  // ===== AÇÕES =====

  /**
   * Manipula ações da tabela
   */
  onActionClick(event: { action: string; item: any }): void {
    const preventiveMaintenance = event.item as PreventiveMaintenance;

    switch (event.action) {
      case 'view':
        this.viewPreventiveMaintenance(preventiveMaintenance.id);
        break;
      case 'edit':
        this.editPreventiveMaintenance(preventiveMaintenance.id);
        break;
      case 'toggle':
        this.toggleStatus(preventiveMaintenance);
        break;
      case 'delete':
        this.deletePreventiveMaintenance(preventiveMaintenance);
        break;
    }
  }

  /**
   * Visualiza uma preventiva
   */
  viewPreventiveMaintenance(id: number): void {
    this.router.navigate(['/operations/preventive-maintenance', id]);
  }

  /**
   * Edita uma preventiva
   */
  editPreventiveMaintenance(id: number): void {
    this.router.navigate(['/operations/preventive-maintenance', id, 'edit']);
  }

  /**
   * Alterna status ativo/inativo
   */
  toggleStatus(preventiveMaintenance: PreventiveMaintenance): void {
    const newStatus = !preventiveMaintenance.isActive;
    const action = newStatus ? 'ativar' : 'desativar';

    if (confirm(`Deseja realmente ${action} esta preventiva?`)) {
      this.isLoading = true;

      this.preventiveMaintenanceService.update(preventiveMaintenance.id, {
        isActive: newStatus
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            preventiveMaintenance.isActive = newStatus;
            this.applyFilters();
            this.isLoading = false;
          },
          error: (err) => {
            console.error('Erro ao alterar status:', err);
            this.error = `Erro ao ${action} preventiva.`;
            this.isLoading = false;
          }
        });
    }
  }

  /**
   * Exclui uma preventiva
   */
  deletePreventiveMaintenance(preventiveMaintenance: PreventiveMaintenance): void {
    if (confirm(`Deseja realmente excluir a preventiva "${preventiveMaintenance.maintenanceName}"?`)) {
      this.isLoading = true;

      this.preventiveMaintenanceService.delete(preventiveMaintenance.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadPreventiveMaintenances();
          },
          error: (err) => {
            console.error('Erro ao excluir preventiva:', err);
            this.error = 'Erro ao excluir preventiva.';
            this.isLoading = false;
          }
        });
    }
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.filteredPreventiveMaintenances = [...this.filteredPreventiveMaintenances].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  /**
   * Cria nova preventiva
   */
  createPreventiveMaintenance(): void {
    this.router.navigate(['/operations/preventive-maintenance/new']);
  }
}

